/**
 * The deck progress-reset floor (`SyncDeckMeta.progressResetAtISO`), applied
 * at the one place every card enters the store: its `setState`.
 *
 * Cards are whole-row LWW, so after "Reset flashcards progress" old history
 * keeps coming back — a row from disk or a cold-deck stream, a peer frame, an
 * offline device's push, an open study session regrading from the history it
 * already held. Every one of those lands through `setState` (the slice
 * mutators, the generic entity broadcasts, the coalesced bulk publish, disk
 * loads, hydrate), so the guard lives there instead of in each of them.
 *
 * - Only decks WITH a floor cost anything; a deck with no floor is a map miss.
 * - Only cards whose reference changed in this tick are checked — unless the
 *   deck's floor itself changed, then its whole bucket is.
 * - It NEVER stamps. A local write that brought old history in was already
 *   stamped by its mutator (so the cleaned row pushes); a server-sourced row
 *   keeps the server's timestamp (so nothing is pushed over a newer row the
 *   server holds — see the local reset action for the rows that must push).
 * - Cleaned rows are re-persisted a microtask later, after the writing path's
 *   own put of the uncleaned row.
 */
import type { StateCreator } from 'zustand';

import { applyProgressFloor } from '../../../algorithms/spaced-repetition/card-utils';
import type { SyncCardProps as CardProps } from '../../../sync-engine/schema/shared-types';
import type { DeckProperties } from '../../provider/sync-storage';
import { getMutationSource } from '../framework/mutation-source';
import type { FlashkaState } from './flashka-store';
import { getSyncStorage, reportSyncStorageError, safeBulkPut } from './store-utils';

type Buckets = Record<string, Record<string, CardProps>>;
type DeckPropsMap = Record<string, DeckProperties>;

const EMPTY_FLOORS: ReadonlyMap<string, string> = new Map();
const floorsByProps = new WeakMap<DeckPropsMap, ReadonlyMap<string, string>>();

/** `deckId → floor` for the decks that have one; one walk per meta-map identity. */
function floorsOf(props: DeckPropsMap | undefined): ReadonlyMap<string, string> {
  if (!props) return EMPTY_FLOORS;
  let floors = floorsByProps.get(props);
  if (!floors) {
    const map = new Map<string, string>();
    for (const id in props) {
      const floor = props[id]?.progressResetAtISO;
      if (floor) map.set(id, floor);
    }
    floors = map.size > 0 ? map : EMPTY_FLOORS;
    floorsByProps.set(props, floors);
  }
  return floors;
}

/** The deck's current floor, if any. */
export function deckProgressFloor(
  state: Pick<FlashkaState, 'deckProperties'>,
  deckId: string,
): string | undefined {
  return state.deckProperties[deckId]?.progressResetAtISO || undefined;
}

/** Decks whose floor differs between two meta maps (set, raised or cleared). */
export function changedProgressFloors(
  prev: DeckPropsMap | undefined,
  next: DeckPropsMap | undefined,
): string[] {
  if (prev === next) return [];
  const before = floorsOf(prev);
  const after = floorsOf(next);
  if (before === after) return [];
  const out: string[] = [];
  for (const [id, floor] of after) if (before.get(id) !== floor) out.push(id);
  for (const id of before.keys()) if (!after.has(id)) out.push(id);
  return out;
}

/** `bucket` with the floor applied — the same object when nothing changed. */
export function applyProgressFloorToBucket(
  bucket: Record<string, CardProps>,
  floorISO: string | undefined,
): Record<string, CardProps> {
  if (!floorISO) return bucket;
  let out: Record<string, CardProps> | null = null;
  for (const id in bucket) {
    const card = bucket[id];
    const floored = applyProgressFloor(card, floorISO);
    if (floored !== card) (out ??= { ...bucket })[id] = floored;
  }
  return out ?? bucket;
}

/**
 * A row that would replace one with a higher floor keeps the higher one when
 * the higher one is still the truth here:
 * - a LOCAL write (a stale spread of an older meta copy) — always;
 * - a SERVER row older than the local one (a peer's broadcast that crossed our
 *   own reset push in flight) — the server keeps the max of what it is pushed.
 * A server row NEWER than the local one wins, floor included: the server has
 * seen our push (a fast clock's floor comes back clamped to server time, and
 * keeping ours would wipe the grades in between). A reset the server has NOT
 * seen is the sync-diff's job ({@link unackedProgressFloorDecks}).
 */
function keepRaisedFloors(
  prev: FlashkaState,
  partial: Partial<FlashkaState>,
): Partial<FlashkaState> {
  const next = partial.deckProperties;
  if (!next || next === prev.deckProperties) return partial;
  const server = getMutationSource() === 'server';
  let patched: DeckPropsMap | null = null;
  for (const id of changedProgressFloors(prev.deckProperties, next)) {
    const local = prev.deckProperties?.[id];
    const kept = local?.progressResetAtISO;
    const row = next[id];
    if (!kept || !row || (row.progressResetAtISO ?? '') >= kept) continue;
    if (server && (row.updatedAtISO ?? '') >= (local.updatedAtISO ?? '')) continue;
    (patched ??= { ...next })[id] = { ...row, progressResetAtISO: kept };
  }
  if (!patched) return partial;
  const persist = changedProgressFloors(next, patched).map((id) => patched![id]);
  queueMicrotask(() => {
    try {
      safeBulkPut(getSyncStorage(), 'deckProperties', persist);
    } catch (err) {
      reportSyncStorageError(err, 'progress-floor-persist-meta');
    }
  });
  return { ...partial, deckProperties: patched };
}

/**
 * Decks whose LOCAL reset has not reached the server yet and that an incoming
 * sync-diff row (with a lower or no floor) is about to replace: the local row
 * is dirty (`updatedAtISO` past its synced timestamp) and carries the higher
 * floor. The diff apply keeps the floor (above) but, as the server's row, it
 * would read as synced — so the caller re-stamps and pushes these.
 */
export function unackedProgressFloorDecks(
  state: Pick<FlashkaState, 'deckProperties'>,
  incoming: Record<string, DeckProperties> | undefined,
  knownTs: Record<string, string>,
): string[] {
  if (!incoming) return [];
  const out: string[] = [];
  for (const id in incoming) {
    const local = state.deckProperties[id];
    const floor = local?.progressResetAtISO;
    if (!floor) continue;
    if ((incoming[id]?.progressResetAtISO ?? '') >= floor) continue;
    const synced = knownTs[`__deckmeta\x01${id}`] ?? '';
    if ((local.updatedAtISO ?? '') > synced) out.push(id);
  }
  return out;
}

/** `partial` with every floored deck's new or changed cards cleaned. */
export function withProgressFloors(
  prev: FlashkaState,
  partial: Partial<FlashkaState>,
): Partial<FlashkaState> {
  if (!partial || (partial.cards === undefined && partial.deckProperties === undefined)) {
    return partial;
  }
  partial = keepRaisedFloors(prev, partial);
  const floors = floorsOf(partial.deckProperties ?? prev.deckProperties);
  if (floors.size === 0) return partial;
  const prevFloors = floorsOf(prev.deckProperties);
  const cards: Buckets = partial.cards ?? prev.cards;
  const prevCards: Buckets = prev.cards ?? {};

  let nextCards: Buckets | null = null;
  const repaired: Array<CardProps & { deckId: string }> = [];
  for (const [deckId, floor] of floors) {
    const bucket = cards[deckId];
    if (!bucket) continue;
    const prevBucket = prevCards[deckId];
    const floorChanged = prevFloors.get(deckId) !== floor;
    if (bucket === prevBucket && !floorChanged) continue;
    let nextBucket: Record<string, CardProps> | null = null;
    for (const id in bucket) {
      const card = bucket[id];
      if (!floorChanged && prevBucket && prevBucket[id] === card) continue;
      const floored = applyProgressFloor(card, floor);
      if (floored === card) continue;
      (nextBucket ??= { ...bucket })[id] = floored;
      repaired.push({ ...floored, deckId });
    }
    if (nextBucket) (nextCards ??= { ...cards })[deckId] = nextBucket;
  }
  if (!nextCards) return partial;

  queueMicrotask(() => {
    try {
      safeBulkPut(getSyncStorage(), 'cards', repaired);
    } catch (err) {
      reportSyncStorageError(err, 'progress-floor-persist');
    }
  });
  return { ...partial, cards: nextCards };
}

type DevtoolsCreator = StateCreator<FlashkaState, [['zustand/devtools', never]], []>;

/**
 * Store middleware: routes every `set` — the slices' AND direct
 * `useFlashkaStore.setState` calls — through {@link withProgressFloors}.
 */
export function progressFloorGuard(initializer: DevtoolsCreator): DevtoolsCreator {
  return (set, get, api) => {
    const guarded = ((partial: unknown, replace?: boolean, ...rest: unknown[]) =>
      (set as (...a: unknown[]) => void)(
        (s: FlashkaState) => {
          const next =
            typeof partial === 'function'
              ? (partial as (s: FlashkaState) => Partial<FlashkaState>)(s)
              : (partial as Partial<FlashkaState>);
          return withProgressFloors(s, next);
        },
        replace,
        ...rest,
      )) as typeof set;
    api.setState = guarded;
    return initializer(guarded, get, api);
  };
}
